/**
 * Invoice Hooks - T095
 *
 * TanStack Query hooks for invoice CRUD operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  CreateInvoiceInput,
  UpdateInvoiceInput,
  InvoiceListQuery as InvoiceListQueryType,
  CreateInvoiceItemInput,
  UpdateInvoiceItemInput,
  MarkInvoicePaidInput,
  UpdateInvoicePaymentDateInput,
  MarkInvoiceSentInput,
  CancelInvoiceInput,
  MarkInvoiceDeferredInput,
  UnDeferInvoiceInput,
  UpdateInvoiceNotesInput,
} from '@armard/shared';

// Re-export for convenience
export type InvoiceListQuery = Partial<InvoiceListQueryType>;

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'deferred';
export type UnitType = 'hours' | 'days' | 'pieces' | 'flat_rate' | 'km';
export type DiscountType = 'percent' | 'absolute';

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  position: number;
  description: string;
  quantity: string;
  unitType: UnitType;
  unitPrice: string;
  discountType: DiscountType | null;
  discountValue: string | null;
  lineTotal: string;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  billingAddressId: string | null;
  billingAddressSnapshot: Record<string, unknown> | null;
  status: InvoiceStatus;
  invoiceDate: string;
  dueDate: string;
  paymentDate: string | null;
  netTotal: string;
  overallDiscountPercent: string | null;
  vatRate: string;
  vatAmount: string;
  grossTotal: string;
  notes: string | null;
  internalNotes: string | null;
  showInternalNotesOnPdf: boolean;
  customerReference: string | null;
  cancellationId: string | null;
  cancelledById: string | null;
  contractId: string | null;
  estimateId: string | null;
  pdfGeneratedAt: string | null;
  pdfStoragePath: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  items: InvoiceItem[];
  customer?: {
    id: string;
    customerNumber: string;
    companyName: string;
    email: string | null;
  };
}

export interface InvoiceListResult {
  invoices: Invoice[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CancelInvoiceResult {
  original: Invoice;
  creditNote: Invoice;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (query: InvoiceListQuery) => [...invoiceKeys.lists(), query] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────
// INVOICE HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list invoices with pagination and filtering
 */
export function useInvoiceList(query: InvoiceListQuery = {}) {
  const fullQuery = {
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    ...query,
  };

  return useQuery({
    queryKey: invoiceKeys.list(fullQuery),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(fullQuery.page));
      params.set('limit', String(fullQuery.limit));
      if (fullQuery.q) params.set('q', fullQuery.q);
      if (fullQuery.customerId) params.set('customerId', fullQuery.customerId);
      if (fullQuery.status) {
        if (Array.isArray(fullQuery.status)) {
          fullQuery.status.forEach((s) => params.append('status', s));
        } else {
          params.set('status', fullQuery.status);
        }
      }
      if (fullQuery.dateFrom) params.set('dateFrom', fullQuery.dateFrom);
      if (fullQuery.dateTo) params.set('dateTo', fullQuery.dateTo);
      if (fullQuery.overdue !== undefined) params.set('overdue', String(fullQuery.overdue));
      if (fullQuery.sortBy) params.set('sortBy', fullQuery.sortBy);
      if (fullQuery.sortOrder) params.set('sortOrder', fullQuery.sortOrder);

      return api.get<InvoiceListResult>(`/invoices?${params.toString()}`);
    },
  });
}

/**
 * Hook to get a single invoice by ID
 */
export function useInvoice(id: string) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => api.get<Invoice>(`/invoices/${id}`),
    enabled: !!id,
  });
}

/**
 * Hook to create an invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInvoiceInput) => api.post<Invoice>('/invoices', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to update an invoice
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateInvoiceInput & { id: string }) =>
      api.patch<Invoice>(`/invoices/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to delete an invoice (drafts only)
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/invoices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to mark invoice as paid
 */
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: MarkInvoicePaidInput & { id: string }) =>
      api.post<Invoice>(`/invoices/${id}/mark-paid`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to correct the payment date of a paid invoice
 */
export function useUpdateInvoicePaymentDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateInvoicePaymentDateInput & { id: string }) =>
      api.patch<Invoice>(`/invoices/${id}/payment-date`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to mark invoice as sent
 */
export function useMarkInvoiceSent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: MarkInvoiceSentInput & { id: string }) =>
      api.post<Invoice>(`/invoices/${id}/mark-sent`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to cancel invoice (creates credit note)
 */
export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: CancelInvoiceInput & { id: string }) =>
      api.post<CancelInvoiceResult>(`/invoices/${id}/cancel`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.original.id), data.original);
      queryClient.setQueryData(invoiceKeys.detail(data.creditNote.id), data.creditNote);
    },
  });
}

/**
 * Hook to mark invoice as deferred (Zurückgestellt)
 */
export function useMarkInvoiceDeferred() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: MarkInvoiceDeferredInput & { id: string }) =>
      api.post<Invoice>(`/invoices/${id}/mark-deferred`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to undefer invoice (Reaktivieren)
 */
export function useUnDeferInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UnDeferInvoiceInput & { id: string }) =>
      api.post<Invoice>(`/invoices/${id}/undefer`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to update internal notes (always allowed regardless of status)
 */
export function useUpdateInvoiceNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateInvoiceNotesInput & { id: string }) =>
      api.patch<Invoice>(`/invoices/${id}/notes`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.setQueryData(invoiceKeys.detail(data.id), data);
    },
  });
}

// ─────────────────────────────────────────────────────────────
// INVOICE ITEM HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to add item to invoice
 */
export function useAddInvoiceItem(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInvoiceItemInput) =>
      api.post<InvoiceItem>(`/invoices/${invoiceId}/items`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to update invoice item
 */
export function useUpdateInvoiceItem(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, ...input }: UpdateInvoiceItemInput & { itemId: string }) =>
      api.patch<InvoiceItem>(`/invoices/${invoiceId}/items/${itemId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to delete invoice item
 */
export function useDeleteInvoiceItem(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => api.delete(`/invoices/${invoiceId}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to reorder invoice items
 */
export function useReorderInvoiceItems(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemIds: string[]) =>
      api.post<InvoiceItem[]>(`/invoices/${invoiceId}/items/reorder`, { itemIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// PDF DOWNLOAD
// ─────────────────────────────────────────────────────────────

/**
 * Download invoice PDF (triggers draft→sent transition)
 */
export function downloadInvoicePdf(invoiceId: string): void {
  window.open(`/api/v1/invoices/${invoiceId}/pdf`, '_blank');
}

/**
 * Preview invoice PDF (no status transition)
 */
export function previewInvoicePdf(invoiceId: string): void {
  window.open(`/api/v1/invoices/${invoiceId}/pdf/preview`, '_blank');
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const statusLabels: Record<InvoiceStatus, string> = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
  deferred: 'Zurückgestellt',
};

export const statusColors: Record<InvoiceStatus, string> = {
  draft: 'bg-amber-500/20 text-amber-400',
  sent: 'bg-blue-500/20 text-blue-400',
  paid: 'bg-green-500/20 text-green-400',
  overdue: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-500/20 text-gray-500',
  deferred: 'bg-orange-500/20 text-orange-400',
};

export const unitTypeLabels: Record<UnitType, string> = {
  hours: 'Stunden',
  days: 'Tage',
  pieces: 'Stück',
  flat_rate: 'Pauschal',
  km: 'Kilometer',
};
